import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { MFASettings } from "@/components/mfa/MFASettings";
import { useAuth } from "@/contexts/AuthContext";
import { useProvider } from "@/contexts/ProviderContext";
import { useJotformPlanMatch } from "@/hooks/useJotformPlanMatch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { determinePlanFlow, getTierIdFromSubscription, type TierId } from "@/lib/planFlow";
import { formatGraceDeadline, getGraceDaysRemaining } from "@/lib/jotformPlanGrace";
import { getBillingStatusLabel, hasActiveBillingAccess, hasBillingHistory } from "@/lib/billingAccess";
import { AlertTriangle, Calendar, CreditCard, Mail, User } from "lucide-react";

const AccountSettingsPage = () => {
  const { user, subscription, isSubscriptionLoading } = useAuth();
  const { connectionStatus } = useProvider();
  const jotformPlanMatch = useJotformPlanMatch();
  const billingHasHistory = hasBillingHistory(subscription);
  const billingAccessActive = hasActiveBillingAccess(subscription);
  const billingSuspended = billingHasHistory && !billingAccessActive;
  const upgradeRequired = Boolean(jotformPlanMatch.data?.upgradeRequired);
  const mismatchEnforced = upgradeRequired && Boolean(jotformPlanMatch.data?.enforcementActive);
  const graceEndsLabel = formatGraceDeadline(jotformPlanMatch.data?.graceEndsAt);
  const graceDaysRemaining = getGraceDaysRemaining(jotformPlanMatch.data?.graceEndsAt);
  const planDecision = determinePlanFlow({
    currentTierId: getTierIdFromSubscription(subscription),
    matchedTierId: (jotformPlanMatch.data?.matchedTier.id as TierId | undefined) || null,
    currentPriceIdKey: subscription?.active_price_id_key || null,
    subscribed: subscription?.subscribed,
    billingHasHistory: subscription?.billing_has_history,
    billingAccessActive: subscription?.billing_access_active,
  });
  const planHref = planDecision.mode === "upgrade" || planDecision.mode === "reinstate"
    ? `/select-tier?action=${planDecision.mode}`
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow">
        <div className="container mx-auto max-w-2xl px-4 py-8">
          <h1 className="mb-6 text-2xl font-bold">Account Settings</h1>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <CardTitle className="text-lg">Account Information</CardTitle>
                </div>
                <CardDescription>Your account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Member since</p>
                    <p className="text-sm text-muted-foreground">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "Unknown"}
                    </p>
                  </div>
                </div>

                {!isSubscriptionLoading && subscription && (
                  <div className="pt-2">
                    <p className="mb-2 text-sm font-medium">Subscription</p>
                    <Badge variant={subscription.subscribed ? "default" : "secondary"}>
                      {subscription.subscription_tier || "Free"} {subscription.billing_has_history ? `- ${getBillingStatusLabel(subscription.billing_status)}` : "Plan"}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {!isSubscriptionLoading && billingSuspended && planHref && (
              <Alert className="border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                <CreditCard className="h-4 w-4" />
                <AlertTitle>Billing inactive</AlertTitle>
                <AlertDescription>
                  FAB is still preserving and taking backups, but interactive access stays limited while billing is {getBillingStatusLabel(subscription?.billing_status).toLowerCase()}.
                  <div className="mt-3">
                    <Button asChild size="sm">
                      <Link to={planHref}>Reinstate Plan</Link>
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {!billingSuspended && connectionStatus.jotform === true && upgradeRequired && !mismatchEnforced && planHref && (
              <Alert className="border-blue-200 bg-blue-50 text-blue-950 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Upgrade grace period active</AlertTitle>
                <AlertDescription>
                  {graceEndsLabel
                    ? `Your connected Jotform account now maps to ${jotformPlanMatch.data?.matchedTier.displayName}. Upgrade FAB by ${graceEndsLabel}${graceDaysRemaining !== null ? ` (${graceDaysRemaining} day${graceDaysRemaining === 1 ? "" : "s"} remaining)` : ""} to keep every form link and backup-history view available.`
                    : `Your connected Jotform account now maps to ${jotformPlanMatch.data?.matchedTier.displayName}. Upgrade FAB within the 14-day grace period to keep every form link and backup-history view available.`}
                  <div className="mt-3">
                    <Button asChild size="sm" variant="outline">
                      <Link to={planHref}>Review Upgrade</Link>
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {!billingSuspended && connectionStatus.jotform === true && upgradeRequired && mismatchEnforced && planHref && (
              <Alert className="border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Upgrade required</AlertTitle>
                <AlertDescription>
                  The Jotform grace period
                  {graceEndsLabel ? ` ended on ${graceEndsLabel}` : " has ended"}.
                  Some forms are now hidden in the FAB UI until billing is upgraded, but backups still continue for all forms.
                  <div className="mt-3">
                    <Button asChild size="sm">
                      <Link to={planHref}>Upgrade FAB</Link>
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <MFASettings />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AccountSettingsPage;
